import {
	AfterViewInit,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ElementRef,
	HostListener,
	inject,
	Input,
	input,
	OnDestroy,
	OnInit,
	output,
	TemplateRef,
	viewChild,
} from "@angular/core";
import { fromEvent, Subject } from "rxjs";
import { debounceTime, filter, takeUntil } from "rxjs/operators";
import { KeyboardService } from "common/keyboard.service";
import { UtilService } from "common/util.service";
import { NgClass, NgTemplateOutlet } from "@angular/common";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { TrimDirective } from "common/trim.directive";
import { AutofocusDirective } from "common/autofocus/autofocus.directive";
import { TranslateModule } from "@ngx-translate/core";

const DEBOUNCE_TIME = 300;

//because of korean 2 char words
const MIN_SEARCH_TERM_LENGTH = 1;

const MAX_SEARCH_TERM_LENGTH = Number.MAX_SAFE_INTEGER;

type CreateAllowedCallback = (args: unknown) => boolean;

@Component({
	selector: "stages-auto-complete",
	templateUrl: "auto-complete.component.html",
	styleUrls: ["./auto-complete.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [
		NgClass,
		ReactiveFormsModule,
		TrimDirective,
		AutofocusDirective,
		FormsModule,
		NgTemplateOutlet,
		TranslateModule,
	],
})
export class AutoCompleteComponent<T> implements OnInit, OnDestroy, AfterViewInit {
	private keyboardService = inject(KeyboardService);
	private changeDetector = inject(ChangeDetectorRef);

	readonly BACK_BUTTON_INDEX = -1;

	readonly browseButtonActive = input<boolean>(false);

	readonly backButtonActive = input<boolean>(true);

	readonly createDependendElementAllowedCallback = input<CreateAllowedCallback>();

	readonly dropDownClass = input<string>();

	readonly focusOnInit = input<boolean>(false);

	// TODO: Skipped for migration because:
	//  This input is used in a control flow expression (e.g. `@if` or `*ngIf`)
	//  and migrating would break narrowing currently.
	@Input()
	inputIconClasses?: string[];

	readonly isDense = input<boolean>(false);

	readonly isLoading = input<boolean>(false);

	readonly minTermLength = input<number>(MIN_SEARCH_TERM_LENGTH);

	readonly maxTermLength = input(MAX_SEARCH_TERM_LENGTH);

	readonly placeholder = input<string>("");

	readonly results = input<T[]>([]);

	// TODO: Skipped for migration because:
	//  Your application code writes to the input. This prevents migration.
	@Input()
	subordinatedResults?: T[];

	readonly searchEngineAvailable = input<boolean>(true);

	// TODO: Skipped for migration because:
	//  This input is used in a control flow expression (e.g. `@if` or `*ngIf`)
	//  and migrating would break narrowing currently.
	@Input()
	searchResultSeparatorTemplate?: TemplateRef<unknown>;

	readonly searchResultTemplate = input<TemplateRef<unknown>>();

	// TODO: Skipped for migration because:
	//  Your application code writes to the input. This prevents migration.
	@Input()
	term: string = "";

	readonly backButtonPressed = output<void>();

	readonly cancelled = output<void>();

	readonly closeButtonPressed = output<void>();

	readonly escapeKeyPressed = output<void>();

	readonly focusLost = output<T>();

	readonly itemSelected = output<T>();

	readonly searchInput = output<string>();

	readonly openBrowserButtonPressed = output<void>();

	readonly submitted = output<T>();

	readonly input = viewChild.required("autoCompleteInput", { read: ElementRef });

	animationParams = {
		enterFrom: 0,
		enterTo: 0,
		leaveFrom: 0,
		leaveTo: 0,
	};

	private destroy$ = new Subject<boolean>();

	dropDownOpen: boolean = false;

	index: number = -1;

	isIE: boolean = false;

	mouseDown: boolean = false;

	constructor() {
		const utilService = inject(UtilService);

		this.isIE = utilService.browserIsIE();
	}

	ngOnInit(): void {
		const allKeys = fromEvent<KeyboardEvent>(this.input().nativeElement, "keyup");

		allKeys
			.pipe(
				filter((e) => !this.isSpecialKey(e)),
				debounceTime(DEBOUNCE_TIME),
				takeUntil(this.destroy$),
			)
			.subscribe(() => this.triggerOnSearchInput());

		allKeys
			.pipe(
				filter((e) => this.isSpecialKey(e)),
				takeUntil(this.destroy$),
			)
			.subscribe((e) => this.onSpecialKey(e));
	}

	ngAfterViewInit(): void {
		if (this.focusOnInit()) {
			this.focus();
		}
	}

	ngOnDestroy(): void {
		this.destroy$.next(true);
	}

	focus(): void {
		this.input().nativeElement.focus();
	}

	@HostListener("document:click", ["$event"])
	onDocumentClick(event: MouseEvent): void {
		const targetElement = event.target;

		if (!(targetElement instanceof Element)) {
			return;
		}

		if (targetElement !== this.input().nativeElement) {
			this.dropDownOpen =
				(this.term && this.term.length > MIN_SEARCH_TERM_LENGTH) || this.isInSubordinatedElementsMode();
		}
	}

	triggerOnSearchInput(): void {
		if (this.isInSubordinatedElementsMode() || (this.term && this.term.length >= MIN_SEARCH_TERM_LENGTH)) {
			this.dropDownOpen = true;
			this.searchInput.emit(this.term);
		} else {
			this.dropDownOpen = false;
		}
	}

	onSpecialKey(event: KeyboardEvent): void {
		if (this.keyboardService.isEscapeKeyPressed(event)) {
			this.triggerEscapeKeyPressed();
		} else if (this.keyboardService.isReturnKeyPressed(event)) {
			this.returnKeyPressed();
		} else if (this.keyboardService.isNavigationKey(event)) {
			if (this.keyboardService.isDownKeyPressed(event)) {
				this.downKeyPressed();
			} else if (this.keyboardService.isUpKeyPressed(event)) {
				this.upKeyPressed();
			} else if (this.keyboardService.isRightKeyPressed(event)) {
				this.rightKeyPressed();
			} else if (this.keyboardService.isLeftKeyPressed(event)) {
				this.leftKeyPressed();
			}
		}
	}

	private triggerEscapeKeyPressed(): void {
		if (this.isInSubordinatedElementsMode()) {
			this.back();
		} else {
			this.resetAnimationParams();
		}
		// TODO: The 'emit' function requires a mandatory void argument
		this.escapeKeyPressed.emit();
	}

	private downKeyPressed(): void {
		this.index++;
		if (
			this.isInSubordinatedElementsMode()
				? this.index > this.subordinatedResults!.length - 1
				: this.index > this.results().length - 1
		) {
			this.index = this.isInSubordinatedElementsMode() && this.backButtonActive() ? this.BACK_BUTTON_INDEX : 0;
		}
	}

	private upKeyPressed(): void {
		this.index--;
		const backButtonActive = this.backButtonActive();
		if (
			(this.isInSubordinatedElementsMode() && backButtonActive && this.index < this.BACK_BUTTON_INDEX) ||
			(this.isInSubordinatedElementsMode() && !backButtonActive && this.index < 0) ||
			(!this.isInSubordinatedElementsMode() && this.index < 0)
		) {
			this.index = this.isInSubordinatedElementsMode()
				? this.subordinatedResults!.length - 1
				: this.results().length - 1;
		}
	}

	isInSubordinatedElementsMode(): boolean {
		return this.subordinatedResults !== undefined;
	}

	private returnKeyPressed(): void {
		if (this.index >= 0 && this.currentIndexExistsInResults()) {
			this.onSelect(
				this.isInSubordinatedElementsMode() ? this.subordinatedResults![this.index] : this.results()[this.index],
			);
		} else if (this.index === this.BACK_BUTTON_INDEX && this.backButtonActive()) {
			this.back();
		}
	}

	private rightKeyPressed(): void {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Remove this comment and fix warnings!
		if (this.index >= 0 && (this.results()[this.index] as any).dependentElementsContainer) {
			this.onSelect(this.results()[this.index]);
		}
	}

	private currentIndexExistsInResults(): boolean {
		return this.isInSubordinatedElementsMode()
			? this.subordinatedResults!.length > this.index
			: this.results().length > this.index;
	}

	private leftKeyPressed(): void {
		if (this.isInSubordinatedElementsMode() && this.backButtonActive()) {
			this.back();
		}
	}

	onSelect(result: T): void {
		this.animationParams = {
			enterFrom: 0,
			enterTo: -100,
			leaveFrom: 0,
			leaveTo: -100,
		};
		this.index = -1;

		// eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Remove this comment and fix warnings!
		if (!(result as any).dependentElementsContainer) {
			this.dropDownOpen = false;
			this.resetSubordinatedElementsMode();
		}
		this.itemSelected.emit(result);
		this.focus();
	}

	onMouseDown(): void {
		this.mouseDown = true;
	}

	onBlur(element: FocusEvent): void {
		if (this.browseButtonActive() && this.isIE) {
			this.mouseDown = false;
			return;
		}
		if (!this.mouseDown && !(element.relatedTarget instanceof Element && element.relatedTarget.id === "open-browser")) {
			// TODO: The 'emit' function requires a mandatory T argument
			this.focusLost.emit(element as unknown as T);
		}
		this.mouseDown = false;
	}

	hasNoResults(): boolean {
		return this.isLoading()
			? false
			: this.isInSubordinatedElementsMode()
				? this.subordinatedResults!.length === 0
				: this.results().length === 0;
	}

	isSelected(index: number): boolean {
		return index === this.index;
	}

	closeSearch(): void {
		this.resetAnimationParams();
		// TODO: The 'emit' function requires a mandatory void argument
		this.closeButtonPressed.emit();
	}

	openBrowser(): void {
		// TODO: The 'emit' function requires a mandatory void argument
		this.openBrowserButtonPressed.emit();
	}

	back(): void {
		this.animationParams = {
			enterFrom: -200,
			enterTo: -100,
			leaveFrom: 0,
			leaveTo: 100,
		};
		this.changeDetector.detectChanges();
		// TODO: The 'emit' function requires a mandatory void argument
		this.backButtonPressed.emit();
	}

	isSpecialKey(event: KeyboardEvent): boolean {
		return (
			this.keyboardService.isEscapeKeyPressed(event) ||
			this.keyboardService.isReturnKeyPressed(event) ||
			this.keyboardService.isNavigationKey(event)
		);
	}

	showListItem(item: unknown, isSubordinated: boolean): boolean {
		const createDependendElementAllowedCallback = this.createDependendElementAllowedCallback();
		if (isSubordinated && createDependendElementAllowedCallback) {
			return createDependendElementAllowedCallback(item);
		}
		return true;
	}

	private resetSubordinatedElementsMode(): void {
		this.subordinatedResults = undefined;
	}

	resetAnimationParams(): void {
		console.log("HAPPENED");
		this.animationParams = {
			enterFrom: 0,
			enterTo: 0,
			leaveFrom: 0,
			leaveTo: 0,
		};
	}
}